import React from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { QuizResult } from "../../shared/types"; // Add this import
import { Recommendation, ChartTooltipContext } from "../../types/chart-types";

interface PDFExportProps {
  results: QuizResult[];
  recommendations: Recommendation[]; // Use the proper type instead of any[]
  resultsRef: React.RefObject<HTMLDivElement>;
}

const PDFExport: React.FC<PDFExportProps> = ({
  results,
  recommendations,
  resultsRef,
}) => {
  const exportToPDF = async () => {
    const currentYear = new Date().getFullYear();
    try {
      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = true;
        button.innerHTML = "⏳ Generating PDF...";
      }

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 15;
      const contentWidth = pageWidth - 2 * margin;

      // Helper function to add a new page if needed
      const checkPageBreak = (currentY: number, neededHeight: number) => {
        if (currentY + neededHeight > pageHeight - margin) {
          pdf.addPage();
          return margin;
        }
        return currentY;
      };

      // Helper function to add footer with branding to each page
      const addFooter = () => {
        // Footer background with solid color
        pdf.setFillColor(21, 42, 83); // RGB values for #152A53
        pdf.rect(0, pageHeight - 25, pageWidth, 25, "F");

        // Footer text
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(
          `© ${currentYear} Inthera Consulting Group. All rights reserved.`,
          pageWidth / 2,
          pageHeight - 15,
          { align: "center" }
        );

        // Add clickable link for website
        pdf.text(
          "Visit us at: intheraconsultinggroup.com",
          pageWidth / 2,
          pageHeight - 8,
          { align: "center" }
        );

        // Add clickable link
        pdf.link(pageWidth / 2 - 25, pageHeight - 12, 50, 8, {
          url: "https://intheraconsultinggroup.com/",
        });
      };

      // Load the logo image
      const loadLogoImage = (): Promise<string> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;

            if (ctx) {
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL("image/png"));
            } else {
              reject("Failed to get canvas context");
            }
          };
          img.onerror = () => reject("Failed to load logo");
          img.src = "/assets/inthera-logo.png"; // Adjust path as needed
        });
      };

      let logoDataUrl: string | null = null;
      try {
        logoDataUrl = await loadLogoImage();
      } catch (error) {
        console.warn("Could not load logo, continuing without it:", error);
      }

      // Title Page with Enhanced Header
      pdf.setFillColor(21, 42, 83); // RGB values for #152A53
      pdf.rect(0, 0, pageWidth, 70, "F");

      // Add Inthera logo if available
      if (logoDataUrl) {
        const logoWidth = 40; // Increased from 25
        const logoHeight = 25; // Increased from 15
        const logoX = pageWidth / 2 - logoWidth / 2;
        const logoY = 15; // Moved down slightly from 10

        pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);

        // Add clickable area for the logo
        pdf.link(logoX, logoY, logoWidth, logoHeight, {
          url: "https://intheraconsultinggroup.com/",
        });
      }

      // Remove the company name text section completely
      // Main title (moved up since no company text)
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(24);
      pdf.text("Automation Recommendations Report", pageWidth / 2, 50, {
        // Moved up from 55
        align: "center",
      });

      pdf.setFontSize(12);
      pdf.text(
        `Generated on ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        60, // Moved up from 65
        { align: "center" }
      );

      let yPosition = 90;

      // Executive Summary
      pdf.setTextColor(31, 41, 55);
      pdf.setFontSize(18);
      pdf.text("Executive Summary", margin, yPosition);
      yPosition += 15;

      pdf.setFontSize(11);
      const summaryText = `This report provides personalized analytics recommendations based on your data requirements. We analyzed ${results.length} survey responses to identify ${recommendations.length} key analytics capabilities that will transform how you understand and use your data.`;
      const summaryLines = pdf.splitTextToSize(summaryText, contentWidth);
      pdf.text(summaryLines, margin, yPosition);
      yPosition += summaryLines.length * 5 + 15;

      // Key Statistics and Analytics
      yPosition = checkPageBreak(yPosition, 50);
      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83); // RGB values for #152A53
      pdf.text("Key Statistics & Analytics", margin, yPosition);
      yPosition += 12;

      const highPriority = recommendations.filter(
        (r) => r.priority === "High"
      ).length;
      const mediumPriority = recommendations.filter(
        (r) => r.priority === "Medium"
      ).length;
      const lowPriority = recommendations.filter(
        (r) => r.priority === "Low"
      ).length;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      // Statistics with better formatting
      pdf.setFillColor(21, 42, 83);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Total Recommendations: ${recommendations.length}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(239, 68, 68);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `High Priority Features: ${highPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(245, 158, 11);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Medium Priority Features: ${mediumPriority}`,
        margin + 8,
        yPosition
      );
      yPosition += 6;

      pdf.setFillColor(16, 185, 129);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(`Low Priority Features: ${lowPriority}`, margin + 8, yPosition);
      yPosition += 15;

      // Analytics breakdown by category
      const categoryBreakdown = recommendations.reduce((acc: any, rec: any) => {
        acc[rec.category] = (acc[rec.category] || 0) + 1;
        return acc;
      }, {});

      pdf.setFontSize(12);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Recommendations by Category:", margin, yPosition);
      yPosition += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(75, 85, 99);
      Object.entries(categoryBreakdown).forEach(([category, count]) => {
        pdf.setFillColor(21, 42, 83);
        pdf.circle(margin + 2, yPosition - 1.5, 0.8, "F");
        pdf.text(`${category}: ${count} features`, margin + 8, yPosition);
        yPosition += 5;
      });
      yPosition += 10;

      // Average recommendation score
      const avgScore =
        recommendations.reduce((sum: number, rec: any) => sum + rec.score, 0) /
        recommendations.length;
      pdf.setFillColor(21, 42, 83);
      pdf.circle(margin + 2, yPosition - 1.5, 1, "F");
      pdf.text(
        `Average Recommendation Score: ${avgScore.toFixed(1)}/100`,
        margin + 8,
        yPosition
      );
      yPosition += 15;

      // Add footer to first page
      addFooter();

      // Capture Charts Section - IMPROVED APPROACH
      yPosition = checkPageBreak(yPosition, 120);
      if (yPosition === margin) {
        addFooter();
      }

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Visual Analytics Dashboard", margin, yPosition);
      yPosition += 15;

      console.log("=== Starting enhanced chart capture process ===");

      let chartsAdded = 0;
      let chartY = yPosition;
      let chartX = margin;
      const chartsPerRow = 2;
      const chartWidth = (contentWidth - 15) / chartsPerRow;
      const chartHeight = 80;

      try {
        // First, try to find and click analytics button to ensure charts are visible
        console.log("Looking for analytics button...");

        // Try multiple ways to find the analytics button
        const possibleButtons = [
          ...Array.from(document.querySelectorAll("button")),
          ...Array.from(document.querySelectorAll('[role="tab"]')),
          ...Array.from(document.querySelectorAll("a")),
          ...Array.from(document.querySelectorAll('[class*="tab"]')),
        ];

        const analyticsButton = possibleButtons.find((button) => {
          const text = button.textContent?.toLowerCase() || "";
          return (
            text.includes("analytics") ||
            text.includes("chart") ||
            text.includes("visual") ||
            text.includes("dashboard")
          );
        });

        if (analyticsButton) {
          console.log("Found analytics button, clicking...");
          (analyticsButton as HTMLElement).click();

          // Give charts time to render
          await new Promise((resolve) => setTimeout(resolve, 2000));
        } else {
          console.log("No analytics button found");
        }

        // Find all chart containers using the analytics-container class
        const chartContainers = document.querySelectorAll(
          ".analytics-container > div"
        );
        console.log(`Found ${chartContainers.length} chart containers`);

        // Chart titles - make sure these match your actual chart titles
        const chartTitles = [
          "Strategic Implementation Timeline",
          "Strategic Impact Score",
          "Strategic Investment Planning",
        ];

        if (chartContainers.length > 0) {
          // Process each chart container
          for (let i = 0; i < chartContainers.length; i++) {
            // Position chart on PDF
            if (chartsAdded > 0 && chartsAdded % chartsPerRow === 0) {
              chartY += chartHeight + 15;
              chartX = margin;
              const newY = checkPageBreak(chartY, chartHeight + 15);
              if (newY === margin) {
                addFooter();
                chartY = newY;
              } else {
                chartY = newY;
              }
            }

            try {
              // Capture entire chart container with html2canvas
              const container = chartContainers[i] as HTMLElement;
              console.log(`Capturing chart container ${i}`);

              const containerCanvas = await html2canvas(container, {
                backgroundColor: "#ffffff",
                scale: 2,
                logging: false,
                useCORS: true,
                allowTaint: true,
              });

              const containerImage = containerCanvas.toDataURL(
                "image/png",
                1.0
              );

              // Check if we got a valid image
              if (containerImage.length < 1000) {
                console.log(`Container ${i} produced empty image`);
                continue;
              }

              // Add chart title
              pdf.setFontSize(10);
              pdf.setTextColor(21, 42, 83);
              pdf.text(chartTitles[i] || `Chart ${i + 1}`, chartX, chartY - 3);

              // Add border
              pdf.setDrawColor(21, 42, 83);
              pdf.setLineWidth(0.5);
              pdf.rect(chartX, chartY, chartWidth, chartHeight);

              // Add chart image to PDF
              pdf.addImage(
                containerImage,
                "PNG",
                chartX + 1,
                chartY + 1,
                chartWidth - 2,
                chartHeight - 2
              );

              chartX += chartWidth + 15;
              chartsAdded++;
              console.log(`Successfully added chart ${i + 1}`);
            } catch (containerError) {
              console.error(`Error processing container ${i}:`, containerError);
              continue;
            }
          }
        } else {
          console.log(
            "No chart containers found, trying to find canvas elements..."
          );

          // Fallback to canvas elements
          const chartElements = document.querySelectorAll("canvas");
          console.log(`Found ${chartElements.length} canvas elements`);

          if (chartElements.length > 0) {
            for (let i = 0; i < Math.min(chartElements.length, 4); i++) {
              // Similar chart processing logic for canvas elements...
              // This is your existing canvas capture code
              // ...
            }
          }
        }

        // Last resort fallback: capture entire results area
        if (chartsAdded === 0 && resultsRef.current) {
          console.log("Trying to capture entire results area...");

          try {
            const entirePageCanvas = await html2canvas(resultsRef.current, {
              backgroundColor: "#ffffff",
              scale: 0.8,
              logging: false,
              useCORS: true,
              allowTaint: true,
              height: 600,
              width: 800,
            });

            const pageImage = entirePageCanvas.toDataURL("image/png", 0.9);

            if (pageImage.length > 1000) {
              pdf.setFontSize(10);
              pdf.setTextColor(21, 42, 83);
              pdf.text("Analytics Overview", margin, chartY - 3);

              pdf.setDrawColor(21, 42, 83);
              pdf.setLineWidth(0.5);
              pdf.rect(margin, chartY, contentWidth, chartHeight * 1.5);

              pdf.addImage(
                pageImage,
                "PNG",
                margin + 1,
                chartY + 1,
                contentWidth - 2,
                chartHeight * 1.5 - 2
              );

              chartsAdded = 1;
              console.log("Successfully captured page overview");
            }
          } catch (pageError) {
            console.error("Error capturing page:", pageError);
          }
        }
      } catch (error) {
        console.error("Chart capture error:", error);
      }

      // Update yPosition based on results
      if (chartsAdded > 0) {
        yPosition = chartY + chartHeight + 25;
        console.log(`Chart capture complete: ${chartsAdded} charts added`);
      } else {
        // Show user-friendly message about missing charts
        pdf.setFontSize(11);
        pdf.setTextColor(75, 85, 99);
        const helpLines = [
          "Charts are not currently visible for capture.",
          "For best results:",
          "• Navigate to the Analytics tab",
          "• Wait for charts to fully load",
          "• Then export the PDF",
        ];

        helpLines.forEach((line, index) => {
          pdf.text(line, margin, yPosition + index * 6);
        });

        yPosition += helpLines.length * 6 + 10;
        console.log("No charts captured - added help text");
      }

      // Quiz Responses Section - START ON NEW PAGE
      addFooter(); // Add footer to current page
      pdf.addPage(); // Start new page
      yPosition = margin; // Reset position for new page

      pdf.setFontSize(16);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Your Survey Responses", margin, yPosition);
      yPosition += 15;

      results.forEach((result, index) => {
        const responseY = checkPageBreak(yPosition, 25);
        if (responseY === margin) {
          addFooter();
          yPosition = responseY;
        } else {
          yPosition = responseY;
        }

        pdf.setFontSize(12);
        pdf.setTextColor(31, 41, 55);
        pdf.text(`Q${index + 1}: ${result.question}`, margin, yPosition);
        yPosition += 8;

        pdf.setFontSize(11);
        pdf.setTextColor(21, 42, 83);
        const answerLines = pdf.splitTextToSize(
          `Answer: ${result.answer}`,
          contentWidth - 10
        );
        pdf.text(answerLines, margin + 5, yPosition);
        yPosition += answerLines.length * 5 + 8;
      });

      // Add footer to current page before new page
      addFooter();

      // Detailed Recommendations Section
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Detailed Automation Recommendations", margin, yPosition);
      yPosition += 20;

      // Sort recommendations by priority for better presentation
      const sortedRecommendations = [...recommendations].sort((a, b) => {
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return (
          priorityOrder[b.priority as keyof typeof priorityOrder] -
          priorityOrder[a.priority as keyof typeof priorityOrder]
        );
      });

      sortedRecommendations.forEach((rec, index) => {
        const recY = checkPageBreak(yPosition, 80);
        if (recY === margin) {
          addFooter();
          yPosition = recY;
        } else {
          yPosition = recY;
        }

        // Priority badge with better styling
        const priorityColor: [number, number, number] =
          rec.priority === "High"
            ? [239, 68, 68]
            : rec.priority === "Medium"
            ? [245, 158, 11]
            : [16, 185, 129];

        pdf.setFillColor(...priorityColor);
        pdf.roundedRect(margin, yPosition - 5, 25, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(8);
        pdf.text(rec.priority.toUpperCase(), margin + 12.5, yPosition, {
          align: "center",
        });

        // Score badge
        pdf.setFillColor(21, 42, 83);
        pdf.roundedRect(margin + 30, yPosition - 5, 20, 8, 2, 2, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.text(`${rec.score}`, margin + 40, yPosition, { align: "center" });

        // Recommendation title
        pdf.setTextColor(31, 41, 55);
        pdf.setFontSize(14);
        pdf.text(`${index + 1}. ${rec.title}`, margin + 55, yPosition);
        yPosition += 12;

        // Category and implementation info
        pdf.setFontSize(10);
        pdf.setTextColor(21, 42, 83);
        pdf.text(`Category: ${rec.category}`, margin, yPosition);
        pdf.text(`Timeline: ${rec.implementation}`, margin + 70, yPosition);
        pdf.text(`ROI: ${rec.roi}`, margin + 140, yPosition);
        yPosition += 10;

        // Description with better formatting
        pdf.setFontSize(10);
        pdf.setTextColor(75, 85, 99);
        const descLines = pdf.splitTextToSize(rec.description, contentWidth);
        pdf.text(descLines, margin, yPosition);
        yPosition += descLines.length * 4 + 8;

        // Benefits section with improved styling
        pdf.setFontSize(11);
        pdf.setTextColor(31, 41, 55);
        pdf.text("Key Benefits:", margin, yPosition);
        yPosition += 6;

        // Benefits background
        const benefitsHeight = rec.benefits.length * 5 + 4;
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(
          margin,
          yPosition - 2,
          contentWidth,
          benefitsHeight,
          2,
          2,
          "F"
        );

        rec.benefits.forEach((benefit: string, benefitIndex: number) => {
          pdf.setFillColor(16, 185, 129);
          pdf.circle(margin + 5, yPosition + 1.5, 1, "F");
          pdf.setTextColor(75, 85, 99);
          pdf.setFontSize(9);
          const benefitLines = pdf.splitTextToSize(benefit, contentWidth - 15);
          pdf.text(benefitLines, margin + 10, yPosition + 3);
          yPosition += benefitLines.length * 4 + 1;
        });

        yPosition += 15; // Spacing between recommendations
      });

      // Add footer to recommendations page
      addFooter();

      // Implementation Roadmap Section
      pdf.addPage();
      yPosition = margin;

      pdf.setFontSize(18);
      pdf.setTextColor(21, 42, 83);
      pdf.text("Implementation Roadmap", margin, yPosition);
      yPosition += 20;

      // Group recommendations by implementation timeline
      const timelineGroups = recommendations.reduce((acc: any, rec: any) => {
        const timeline = rec.implementation || "Unknown";
        if (!acc[timeline]) acc[timeline] = [];
        acc[timeline].push(rec);
        return acc;
      }, {});

      Object.entries(timelineGroups).forEach(
        ([timeline, recs]: [string, any]) => {
          const timelineY = checkPageBreak(yPosition, 40);
          if (timelineY === margin) {
            addFooter();
            yPosition = timelineY;
          } else {
            yPosition = timelineY;
          }

          pdf.setFillColor(21, 42, 83);
          pdf.roundedRect(margin, yPosition - 5, contentWidth, 12, 2, 2, "F");
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(12);
          pdf.text(
            `${timeline} (${recs.length} features)`,
            margin + 5,
            yPosition + 2
          );
          yPosition += 18;

          recs.forEach((rec: any, index: number) => {
            pdf.setFontSize(10);
            pdf.setTextColor(31, 41, 55);
            pdf.text(`${index + 1}. ${rec.title}`, margin + 5, yPosition);
            pdf.setTextColor(75, 85, 99);
            pdf.text(`Score: ${rec.score}/100`, margin + 120, yPosition);
            pdf.text(`Priority: ${rec.priority}`, margin + 160, yPosition);
            yPosition += 6;
          });
          yPosition += 10;
        }
      );

      // Add footer to final page
      addFooter();

      pdf.save("inthera-analytics-comprehensive-analysis-report.pdf");

      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating the PDF. Please try again.");

      const button = document.querySelector(
        "[data-pdf-export]"
      ) as HTMLButtonElement;
      if (button) {
        button.disabled = false;
        button.innerHTML = "📊 Export Comprehensive Report";
      }
    }
  };

  return (
    <button
      data-pdf-export
      onClick={exportToPDF}
      style={{
        padding: "1rem 2rem",
        background: "rgb(201, 167, 80)",
        color: "white",
        border: "none",
        borderRadius: "12px",
        cursor: "pointer",
        fontSize: "1.1rem",
        fontWeight: "600",
        transition: "all 0.3s ease",
        boxShadow: "0 4px 15px rgba(201, 167, 80, 0.3)",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 20px rgba(201, 167, 80, 0.4)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 15px rgba(201, 167, 80, 0.3)";
      }}
    >
      📊 Export Comprehensive Report
    </button>
  );
};

export default PDFExport;
